import { v4 as uuidv4 } from "uuid";

const generatePrompt = (description, questionCounts) => {
  const totalQuestions = Object.values(questionCounts).reduce(
    (a, b) => a + b,
    0
  );

  let prompt = `Generate ${totalQuestions} questions based on this description: "${description}"\n\n`;
  prompt +=
    "For each question type, provide the following number of questions:\n";

  // Only add selected question types to the prompt
  Object.entries(questionCounts).forEach(([type, count]) => {
    if (count > 0) {
      prompt += `- ${count} ${type} question(s)\n`;
    }
  });

  prompt +=
    "\nIMPORTANT: Only provide the JSON code in your response. Do not include any additional text, comments, or explanations before or after the JSON structure. I should be able to copy this response.\n";

  // Start JSON structure
  prompt += `\nFormat your response in JSON with the following structure for each question type:\n{`;

  // Only include JSON structure for selected question types
  if (questionCounts["Multiple Choice"] > 0) {
    prompt += `
    "Multiple Choice": [{
      "question": "string",
      "options": ["string"],
      "correctAnswers": [{"index": number, "text": "string"}]
    }],`;
  }

  if (questionCounts["Multiple Checkbox"] > 0) {
    prompt += `
    "Multiple Checkbox": [{
      "question": "string",
      "options": ["string"],
      "correctAnswers": [{"index": number, "text": "string"}]
    }],`;
  }

  if (questionCounts["Fill in the Blanks"] > 0) {
    prompt += `
    "Fill in the Blanks": [{
      "question": "string (include the correct word in brackets, e.g., 'I am [going] to Miami.')",
      "correctAnswers": ["string (just the word without brackets, e.g., 'going'. Remember not to use parantheses, use brackets instead.)"]
    }],`;
  }

  if (questionCounts["Pairing"] > 0) {
    prompt += `
    "Pairing": [{
      "question": "string",
      "leftItems": ["string"],
      "rightItems": ["string"],
      "pairs": [{"left": "string", "right": "string"}]
    }],`;
  }

  if (questionCounts["Sentence Ordering"] > 0) {
    prompt += `
    "Sentence Ordering": [{
      "question": "string (provide a properly ordered full sentence that will be used for word ordering practice)"
    }],`;
  }

  if (questionCounts["Spell the Word"] > 0) {
    prompt += `
    "Spell the Word": [{
      "sentence": "string (provide a complete sentence with one word in square brackets that needs to be spelled, e.g., 'I am eating an [apple] for breakfast. If the format is using parantheses '()' or '______', convert the sentence to the proper format with square brackets.')",
      "correctAnswers": ["string (the word without brackets, e.g., ['apple'])."]
    }],`;
  }

  if (questionCounts["Open-Ended Text"] > 0) {
    prompt += `
    "Open-Ended Text": [{
      "content": "string"
    }],`;
  }

  if (questionCounts["Audio Recording Task"] > 0) {
    prompt += `
    "Audio Recording Task": [{
      "question": "string (provide clear instructions for what the student should record)",
      "type": "audio-recorder",
      "border": "shadow"
    }],`;
  }

  // Remove trailing comma and close the JSON structure
  prompt = prompt.replace(/,\s*$/, "") + "\n}";

  return prompt;
};

const processAIResponse = (aiResponse) => {
  try {
    let cleanedResponse = aiResponse.trim();

    // Handle cases like ```json\n...\n```
    if (
      cleanedResponse.startsWith("```json\n") &&
      cleanedResponse.endsWith("\n```")
    ) {
      cleanedResponse = cleanedResponse.substring(
        7,
        cleanedResponse.length - 4
      );
    }
    // Handle cases like ```json...```
    else if (
      cleanedResponse.startsWith("```json") &&
      cleanedResponse.endsWith("```")
    ) {
      cleanedResponse = cleanedResponse.substring(
        7,
        cleanedResponse.length - 3
      );
    }
    // Handle cases like ```...```
    else if (
      cleanedResponse.startsWith("```") &&
      cleanedResponse.endsWith("```")
    ) {
      cleanedResponse = cleanedResponse.substring(
        3,
        cleanedResponse.length - 3
      );
    }

    const parsed = JSON.parse(cleanedResponse.trim()); // Trim again in case fences had extra spaces

    return Object.entries(parsed).flatMap(([type, questions]) =>
      questions.map((question) => {
        if (type === "Multiple Choice" || type === "Multiple Checkbox") {
          return {
            type: "questions",
            questionType: type,
            questionId: uuidv4(),
            question: question.question,
            options: question.options || [],
            correctAnswers: question.correctAnswers.map(
              (answer) => answer.index
            ),
            score: 1,
            border: "shadow",
          };
        }

        if (type === "Fill in the Blanks") {
          return {
            type: "questions",
            questionType: type,
            questionId: uuidv4(),
            question: question.question,
            correctAnswers: question.correctAnswers,
            score: 1,
            border: "shadow",
          };
        }

        if (type === "Audio Recording Task") {
          return {
            type: "audio-recorder",
            questionId: uuidv4(),
            question: question.question,
            score: 1,
            border: "shadow",
          };
        }

        // Special handling for Pairing questions
        if (type === "Pairing") {
          return {
            type: "questions",
            questionType: type,
            questionId: uuidv4(),
            question: question.question,
            pairs: question.pairs || [],
            leftItems: question.leftItems || [],
            rightItems: question.rightItems || [],
            score: 1,
            border: "shadow",
          };
        }

        if (type === "Sentence Ordering") {
          return {
            type: "questions",
            questionType: type,
            questionId: uuidv4(),
            question: question.question,
            sentences: [],
            correctAnswers: [],
            score: 1,
            border: "shadow",
          };
        }

        if (type === "Spell the Word") {
          return {
            type: "questions",
            questionType: type,
            questionId: uuidv4(),
            sentence: question.sentence,
            correctAnswers: Array.isArray(question.correctAnswers)
              ? question.correctAnswers
              : [question.correctAnswers],
            score: 1,
            border: "shadow",
          };
        }

        return {
          type: "questions",
          questionType: type,
          questionId: uuidv4(),
          ...question,
          score: 1,
          border: "shadow",
          options: question.options || [],
          correctAnswers: question.correctAnswers || [],
          questionCategories: [],
        };
      })
    );
  } catch (error) {
    console.error("Error processing AI response:", error);
    throw new Error("Failed to process AI response");
  }
};

export { generatePrompt, processAIResponse };
